<template>
	<div class="news-listing">
		<!-- Search & Sort Bar -->
		<div class="news-listing__controls">
			<div class="container">
				<div class="row g-3 align-items-center">
					<div class="col-12 col-md">
						<div class="news-listing__search">
							<i class="bi bi-search"></i>
							<input
								v-model="searchQuery"
								type="text"
								class="form-control"
								placeholder="Search product news..."
								@input="debouncedSearch"
							/>
							<button
								v-if="searchQuery"
								@click="clearSearch"
								class="news-listing__clear"
								aria-label="Clear search"
							>
								<i class="bi bi-x-circle"></i>
							</button>
						</div>
					</div>
					<div class="col-12 col-md-auto">
						<select
							v-model="sortOrder"
							@change="fetchProductNews(1)"
							class="form-select"
						>
							<option value="newest">Newest First</option>
							<option value="oldest">Oldest First</option>
						</select>
					</div>
				</div>
			</div>
		</div>

		<!-- Loading State -->
		<div v-if="loading && !items.length" class="news-listing__loading">
			<div class="container text-center py-5">
				<div class="spinner-border text-primary" role="status">
					<span class="visually-hidden">Loading...</span>
				</div>
			</div>
		</div>

		<!-- No Results -->
		<div v-else-if="!loading && hasSearched && !items.length" class="news-listing__empty">
			<div class="container text-center py-5">
				<i class="bi bi-newspaper"></i>
				<h3>No product news found</h3>
				<p v-if="searchQuery">
					No results for "{{ searchQuery }}". Try different keywords.
				</p>
				<p v-else>No product news items available.</p>
			</div>
		</div>

		<!-- News Items -->
		<div v-else class="news-listing__items">
			<div v-for="item in items" :key="item.id" class="news-item">
				<div class="container">
					<!-- Image -->
					<div class="news-item__image">
						<img v-if="item.image" :src="item.image" :alt="item.title" loading="lazy" />
						<div v-else class="news-item__placeholder">
							<i class="bi bi-newspaper"></i>
						</div>
					</div>

					<!-- Content -->
					<div class="news-item__content">
						<span class="news-item__date">{{ item.date }}</span>
						<h2 class="news-item__title">{{ item.title }}</h2>
						<div class="news-item__body" v-html="item.content"></div>
					</div>
				</div>
			</div>
		</div>

		<!-- Pagination -->
		<div v-if="maxPages > 1" class="news-listing__pagination">
			<div class="container">
				<nav aria-label="Product news pagination">
					<ul class="pagination justify-content-center">
						<li class="page-item" :class="{ disabled: currentPage === 1 }">
							<button
								class="page-link"
								@click="fetchProductNews(currentPage - 1)"
								:disabled="currentPage === 1"
							>
								<i class="bi bi-chevron-left"></i> Previous
							</button>
						</li>

						<li
							v-for="page in visiblePages"
							:key="page"
							class="page-item"
							:class="{ active: page === currentPage }"
						>
							<button class="page-link" @click="fetchProductNews(page)">
								{{ page }}
							</button>
						</li>

						<li class="page-item" :class="{ disabled: currentPage === maxPages }">
							<button
								class="page-link"
								@click="fetchProductNews(currentPage + 1)"
								:disabled="currentPage === maxPages"
							>
								Next <i class="bi bi-chevron-right"></i>
							</button>
						</li>
					</ul>
				</nav>
			</div>
		</div>
	</div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';

export default {
	name: 'ProductNewsListing',
	setup() {
		const items = ref([]);
		const loading = ref(false);
		const hasSearched = ref(false);
		const searchQuery = ref('');
		const sortOrder = ref('newest');
		const currentPage = ref(1);
		const maxPages = ref(1);
		const totalItems = ref(0);

		let searchDebounce = null;

		const visiblePages = computed(() => {
			const pages = [];
			const total = maxPages.value;
			const current = currentPage.value;

			let start = Math.max(1, current - 2);
			let end = Math.min(total, current + 2);

			// Ensure we show at least 5 pages when possible
			if (end - start < 4) {
				if (start === 1) {
					end = Math.min(total, start + 4);
				} else {
					start = Math.max(1, end - 4);
				}
			}

			for (let i = start; i <= end; i++) {
				pages.push(i);
			}

			return pages;
		});

		const fetchProductNews = async (page = 1) => {
			loading.value = true;
			hasSearched.value = true;
			currentPage.value = page;

			try {
				const params = new URLSearchParams({
					action: 'get_product_news_items',
					search: searchQuery.value.trim(),
					sort: sortOrder.value,
					page: page,
				});

				const ajaxUrl = window.jaeckleData?.ajaxUrl || '/wp-admin/admin-ajax.php';
				const response = await fetch(`${ajaxUrl}?${params}`);

				if (!response.ok) {
					throw new Error('Failed to fetch product news');
				}

				const data = await response.json();

				if (data.success) {
					items.value = data.data.items;
					maxPages.value = data.data.max_pages;
					totalItems.value = data.data.total;
				}
			} catch (err) {
				console.error('Error fetching product news:', err);
			} finally {
				loading.value = false;
			}

			// Scroll to top of listing
			if (page > 1) {
				const el = document.querySelector('.news-listing');
				if (el) {
					el.scrollIntoView({ behavior: 'smooth', block: 'start' });
				}
			}
		};

		const debouncedSearch = () => {
			clearTimeout(searchDebounce);
			searchDebounce = setTimeout(() => {
				fetchProductNews(1);
			}, 500);
		};

		const clearSearch = () => {
			searchQuery.value = '';
			fetchProductNews(1);
		};

		onMounted(() => {
			const params = new URLSearchParams(window.location.search);
			const queryParam = params.get('search') || params.get('q') || '';
			searchQuery.value = queryParam.trim();
			fetchProductNews(1);
		});

		return {
			items,
			loading,
			hasSearched,
			searchQuery,
			sortOrder,
			currentPage,
			maxPages,
			totalItems,
			visiblePages,
			fetchProductNews,
			debouncedSearch,
			clearSearch,
		};
	},
};
</script>
